import { Control, Controller, FieldErrors } from 'react-hook-form'
import { Box, Chip, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import type { ScoringKnobDto, ScoringOptionKey, ScoringOptionsDto } from '@api/types/scoring'

interface ScoringKnobFieldProps {
  knob: ScoringKnobDto
  field: ScoringOptionKey
  control: Control<ScoringOptionsDto>
  errors: FieldErrors<ScoringOptionsDto>
  /** Current form value, so we can show whether it differs from the default. */
  value: number
  onResetToDefault: () => void
  disabled?: boolean
}

/**
 * One tunable knob: a number input carrying the server's own help text, bounds and default.
 * Everything shown here comes from the API's knob metadata, so the UI can't drift from the policy.
 */
export function ScoringKnobField({
  knob,
  field,
  control,
  errors,
  value,
  onResetToDefault,
  disabled,
}: ScoringKnobFieldProps) {
  const isModified = !Number.isNaN(value) && value !== knob.default
  const error = errors[field]

  return (
    <Box sx={{ mb: 3 }}>
      <Controller
        name={field}
        control={control}
        render={({ field: controllerField }) => (
          <TextField
            {...controllerField}
            type="number"
            fullWidth
            disabled={disabled}
            label={knob.name}
            error={!!error}
            helperText={error?.message ?? knob.description}
            inputProps={{
              min: knob.min,
              max: knob.max,
              step: knob.dataType === 'integer' ? 1 : 0.05,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* On a phone the chip ate most of the input; there it sits under the field. */}
                    {isModified && (
                      <Chip
                        label="Modified"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                      />
                    )}
                    <Tooltip title={`Reset to default (${knob.default})`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={onResetToDefault}
                          disabled={disabled || !isModified}
                          aria-label={`Reset ${knob.name} to default`}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      {isModified && (
        <Chip
          label={`Modified (default ${knob.default})`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, mt: 0.5 }}
        />
      )}
    </Box>
  )
}
